export interface AnalyticsEvent {
  id: string;
  type: 'page_view' | 'shipment_search' | 'quote_request' | 'conversion' | 'user_action';
  timestamp: number;
  data: Record<string, any>;
  sessionId: string;
}

export interface AnalyticsMetrics {
  totalPageViews: number;
  totalShipmentSearches: number;
  totalQuoteRequests: number;
  totalConversions: number;
  uniqueVisitors: number;
  averageSessionDuration: number;
  topPages: { page: string; views: number }[];
  topSearchRoutes: { route: string; count: number }[];
  conversionRate: number;
}

class AnalyticsService {
  private storageKey = 'shiptrack_analytics';
  private sessionKey = 'shiptrack_session';
  private sessionId: string;

  constructor() {
    this.sessionId = this.getOrCreateSession();
  }

  private getOrCreateSession(): string {
    if (typeof window === 'undefined') return '';
    
    let session = sessionStorage.getItem(this.sessionKey);
    if (!session) {
      session = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(this.sessionKey, session);
    }
    return session;
  }

  private getEvents(): AnalyticsEvent[] {
    if (typeof window === 'undefined') return [];
    
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  private saveEvents(events: AnalyticsEvent[]): void {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(events));
    } catch (error) {
      console.error('Failed to save analytics:', error);
    }
  }

  trackPageView(page: string): void {
    this.trackEvent('page_view', { page });
  }

  trackShipmentSearch(trackingNumber: string, origin?: string, destination?: string): void {
    this.trackEvent('shipment_search', {
      trackingNumber,
      route: origin && destination ? `${origin} → ${destination}` : 'unknown',
    });
  }

  trackQuoteRequest(data: { name: string; email: string; subject: string }): void {
    this.trackEvent('quote_request', data);
  }

  trackConversion(type: string, value?: number): void {
    this.trackEvent('conversion', { type, value });
  }

  trackUserAction(action: string, data?: Record<string, any>): void {
    this.trackEvent('user_action', { action, ...data });
  }

  private trackEvent(type: AnalyticsEvent['type'], data: Record<string, any>): void {
    const event: AnalyticsEvent = {
      id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      timestamp: Date.now(),
      data,
      sessionId: this.sessionId,
    };

    const events = this.getEvents();
    events.push(event);

    // Keep only last 1000 events
    if (events.length > 1000) {
      events.splice(0, events.length - 1000);
    }

    this.saveEvents(events);
  }

  getMetrics(days: number = 7): AnalyticsMetrics {
    const events = this.getEvents();
    const cutoffTime = Date.now() - days * 24 * 60 * 60 * 1000;
    const recentEvents = events.filter((e) => e.timestamp >= cutoffTime);

    const pageViews = recentEvents.filter((e) => e.type === 'page_view');
    const shipmentSearches = recentEvents.filter((e) => e.type === 'shipment_search');
    const quoteRequests = recentEvents.filter((e) => e.type === 'quote_request');
    const conversions = recentEvents.filter((e) => e.type === 'conversion');

    const uniqueSessions = new Set(recentEvents.map((e) => e.sessionId));

    // Calculate top pages
    const pageCount: Record<string, number> = {};
    pageViews.forEach((e) => {
      const page = e.data.page || 'unknown';
      pageCount[page] = (pageCount[page] || 0) + 1;
    });
    const topPages = Object.entries(pageCount)
      .map(([page, views]) => ({ page, views }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 5);

    // Calculate top search routes
    const routeCount: Record<string, number> = {};
    shipmentSearches.forEach((e) => {
      const route = e.data.route || 'unknown';
      if (route !== 'unknown') {
        routeCount[route] = (routeCount[route] || 0) + 1;
      }
    });
    const topSearchRoutes = Object.entries(routeCount)
      .map(([route, count]) => ({ route, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Calculate average session duration
    const sessionDurations: Record<string, { start: number; end: number }> = {};
    recentEvents.forEach((e) => {
      if (!sessionDurations[e.sessionId]) {
        sessionDurations[e.sessionId] = { start: e.timestamp, end: e.timestamp };
      } else {
        sessionDurations[e.sessionId].end = Math.max(sessionDurations[e.sessionId].end, e.timestamp);
      }
    });
    const durations = Object.values(sessionDurations).map((s) => s.end - s.start);
    const averageSessionDuration = durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;

    // Calculate conversion rate
    const conversionRate = pageViews.length > 0 ? (conversions.length / pageViews.length) * 100 : 0;

    return {
      totalPageViews: pageViews.length,
      totalShipmentSearches: shipmentSearches.length,
      totalQuoteRequests: quoteRequests.length,
      totalConversions: conversions.length,
      uniqueVisitors: uniqueSessions.size,
      averageSessionDuration,
      topPages,
      topSearchRoutes,
      conversionRate,
    };
  }

  getAllEvents(): AnalyticsEvent[] {
    return this.getEvents();
  }

  clearData(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(this.storageKey);
  }
}

export const analytics = new AnalyticsService();